import { createClient } from '@supabase/supabase-js';
import type { Database } from '../index';
import type { 
  Product,
  User,
  UserRole,
  InquiryStatus,
  UpdateNewsInput,
  AboutContent,
} from '../index';

// Supabase client configuration
// These should be set as environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not found. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.');
}

// Create the Supabase client with TypeScript types
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Type helpers for database operations - use Database types directly
type Tables = Database['public']['Tables'];
type ProductUpdate = Tables['products']['Update'];
type ProductCategoryInsert = Tables['product_categories']['Insert'];
type ProductCategoryUpdate = Tables['product_categories']['Update'];
type NewsInsert = Tables['news']['Insert'];
type GalleryInsert = Tables['gallery']['Insert'];
type GalleryUpdate = Tables['gallery']['Update'];
type PartnerInsert = Tables['partners']['Insert'];
type PartnerUpdate = Tables['partners']['Update'];
type TeamMemberInsert = Tables['team_members']['Insert'];
type TeamMemberUpdate = Tables['team_members']['Update'];
type InquiryInsert = Tables['inquiries']['Insert'];
type InquiryUpdate = Tables['inquiries']['Update'];
type ContactMessageInsert = Tables['contact_messages']['Insert'];
type NotificationInsert = Tables['notifications']['Insert'];

// Helper function to get the current user
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
}

// Helper function to get the current user's profile from public.users
export async function getCurrentUserProfile() {
  const user = await getCurrentUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // No profile found
    throw error;
  }
  return data as User | null;
}

// Helper function to check if user has a specific role
export async function hasRole(requiredRoles: string[]) {
  const profile = await getCurrentUserProfile();
  if (!profile) return false;
  return requiredRoles.includes(profile.role);
}

// Helper function to check if user is admin (super_admin or manager)
export async function isAdmin() {
  return hasRole(['super_admin', 'manager']);
}

// Helper function to check if user is content manager or above
export async function isContentManager() {
  return hasRole(['super_admin', 'manager', 'content_manager']);
}

// Realtime subscription helpers
export function subscribeToNotifications(callback: (payload: any) => void) {
  return supabase
    .channel('notifications')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
      },
      callback
    )
    .subscribe();
}

export function subscribeToInquiries(callback: (payload: any) => void) {
  return supabase
    .channel('inquiries')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'inquiries',
      },
      callback
    )
    .subscribe();
}

export function subscribeToContactMessages(callback: (payload: any) => void) {
  return supabase
    .channel('contact_messages')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'contact_messages',
      },
      callback
    )
    .subscribe();
}

// Storage helpers
export async function uploadFile(
  bucket: string,
  path: string,
  file: File,
  options?: { upsert?: boolean }
) {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      upsert: options?.upsert ?? false,
      contentType: file.type,
    });

  if (error) throw error;
  return data;
}

export async function deleteFile(bucket: string, paths: string[]) {
  const { error } = await supabase.storage.from(bucket).remove(paths);
  if (error) throw error;
}

export function getPublicUrl(bucket: string, path: string) {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

export async function getPublicChatMessages(conversationId: string, chatToken: string) {
  // @ts-ignore - RPC type is added by the live chat migration.
  const { data, error } = await supabase.rpc('get_public_chat_messages', {
    p_conversation_id: conversationId,
    p_chat_token: chatToken,
  });
  if (error) throw error;
  return (data ?? []) as Array<{
    id: string;
    message: string;
    status: string;
    reply_message: string | null;
    sender: string;
    created_at: string;
  }>;
}

// Database query helpers with proper typing
export const db = {
  feedback: {
    async getAll(filters?: { status?: string }) {
      let query = supabase.from('public_feedback').select('*').order('created_at', { ascending: false });
      if (filters?.status) query = query.eq('status', filters.status);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    async updateStatus(id: string, status: 'pending' | 'approved' | 'rejected') {
      // @ts-ignore - table type is added by the feedback migration.
      const { data, error } = await supabase.from('public_feedback').update({ status }).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    async delete(id: string) {
      const { error } = await supabase.from('public_feedback').delete().eq('id', id);
      if (error) throw error;
    },
    async getApproved(contentType: 'product' | 'news' | 'service', contentId: string) {
      const { data, error } = await supabase.from('public_feedback').select('*').eq('content_type', contentType).eq('content_id', contentId).eq('status', 'approved').order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    async submit(input: { content_type: 'product' | 'news' | 'service'; content_id: string; author_name: string; author_email: string; rating?: number | null; comment: string }) {
      // @ts-ignore - table type is added by the feedback migration.
      const { error } = await supabase.from('public_feedback').insert({ ...input, status: 'pending' });
      if (error) throw error;
    },
  },
  about: {
    async get() {
      const { data, error } = await supabase.from('about_content').select('*').eq('id', 'main').maybeSingle();
      if (error) throw error;
      return data as AboutContent | null;
    },
    async upsert(content: Omit<AboutContent, 'updated_at'>) {
      // The checked-in Supabase type snapshot predates the About migration.
      // @ts-ignore - generated table types will be refreshed after migration.
      const { data, error } = await supabase.from('about_content').upsert({ ...content, id: 'main', updated_at: new Date().toISOString() }).select().single();
      if (error) throw error;
      return data as AboutContent;
    },
    async saveSection(content: Partial<Omit<AboutContent, 'id' | 'updated_at'>>) {
      // The checked-in Supabase type snapshot predates the About migration.
      // @ts-ignore - generated table types will be refreshed after migration.
      const { data, error } = await supabase.from('about_content').upsert({ ...content, id: 'main', updated_at: new Date().toISOString() }).select().single();
      if (error) throw error;
      return data as AboutContent;
    },
  },
  // Products
  products: {
    async getAll(filters?: { category_id?: string; status?: string; featured?: boolean; limit?: number; offset?: number }) {
      let query = supabase
        .from('products')
        .select(`
          *,
          category:product_categories(*)
        `)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (filters?.category_id) query = query.eq('category_id', filters.category_id);
      if (filters?.status) query = query.eq('status', filters.status);
      if (filters?.featured !== undefined) query = query.eq('featured', filters.featured);
      if (filters?.limit) query = query.limit(filters.limit);
      if (filters?.offset) query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    async getById(id: string) {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          category:product_categories(*)
        `)
        .eq('id', id)        .eq('status', 'available')        .single();

      if (error) throw error;
      return data;
    },

    async getBySlug(slug: string) {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          category:product_categories(*)
        `)
        .eq('slug', slug)
        .eq('status', 'available')
        .single();

      if (error) throw error;
      return data;
    },

    async create(product: Omit<Product, 'id' | 'created_at' | 'updated_at' | 'published_at'>) {
      // @ts-ignore - Supabase type inference issue with generic from()
      const { data, error } = await supabase
        .from('products')
        // @ts-ignore - Supabase type inference issue
        .insert(product)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async update(id: string, updates: ProductUpdate) {
      // @ts-ignore - Supabase type inference issue with generic from()
      const { data, error } = await supabase
        .from('products')
        // @ts-ignore - Supabase type inference issue
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async delete(id: string) {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
    },
  },

  // Product Categories
  categories: {
    async getAll() {
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data;
    },

    async create(category: ProductCategoryInsert) {
      const { data, error } = await supabase
        .from('product_categories')
        // @ts-ignore - Supabase table inference resolves this insert as never
        .insert(category)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

async update(id: string, updates: ProductCategoryUpdate) {
      const { data, error } = await supabase
        .from('product_categories')
        // @ts-ignore - Supabase table inference resolves this update as never
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async delete(id: string) {
      const { error } = await supabase.from('product_categories').delete().eq('id', id);
      if (error) throw error;
    },
  },

  // News
  news: {
    async getAll(filters?: { status?: string; featured?: boolean; limit?: number; offset?: number }) {
      let query = supabase
        .from('news')
        .select(`
          *,
          author:users(id, name, avatar_url)
        `)
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (filters?.status) query = query.eq('status', filters.status);
      if (filters?.featured !== undefined) query = query.eq('featured', filters.featured);
      if (filters?.limit) query = query.limit(filters.limit);
      if (filters?.offset) query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    async getById(id: string) {
      const { data, error } = await supabase
        .from('news')
        .select(`
          *,
          author:users(id, name, avatar_url)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },

    async getBySlug(slug: string) {
      const { data, error } = await supabase
        .from('news')
        .select(`
          *,
          author:users(id, name, avatar_url)
        `)
        .eq('slug', slug)
          .eq('status', 'published')
        .single();

      if (error) throw error;
      return data;
    },

    async create(news: NewsInsert) {
      const { data, error } = await supabase
        .from('news')
        // @ts-ignore - Supabase table inference resolves this insert as never
        .insert(news)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async update(id: string, updates: UpdateNewsInput) {
      const { data, error } = await supabase
        .from('news')
        // @ts-ignore - Supabase table inference resolves this update as never
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async delete(id: string) {
      const { error } = await supabase.from('news').delete().eq('id', id);
      if (error) throw error;
    },
  },

  // Gallery
  gallery: {
    async getAll(filters?: { category?: string; is_published?: boolean; limit?: number; offset?: number }) {
      let query = supabase
        .from('gallery')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (filters?.category) query = query.eq('category', filters.category);
      if (filters?.is_published !== undefined) query = query.eq('is_published', filters.is_published);
      if (filters?.limit) query = query.limit(filters.limit);
      if (filters?.offset) query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    async create(item: GalleryInsert) {
      const { data, error } = await supabase
        .from('gallery')
        // @ts-ignore - Supabase table inference resolves this insert as never
        .insert(item)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async update(id: string, updates: GalleryUpdate) {
      const { data, error } = await supabase
        .from('gallery')
        // @ts-ignore - Supabase table inference resolves this update as never
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async delete(id: string) {
      const { error } = await supabase.from('gallery').delete().eq('id', id);
      if (error) throw error;
    },
  },

  // Partners
  partners: {
    async getAll(filters?: { type?: string; is_active?: boolean }) {
      let query = supabase
        .from('partners')
        .select('*')
        .order('sort_order', { ascending: true });

      if (filters?.type) query = query.eq('type', filters.type);
      if (filters?.is_active !== undefined) query = query.eq('is_active', filters.is_active);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    async create(partner: PartnerInsert) {
      const { data, error } = await supabase
        .from('partners')
        // @ts-ignore - Supabase table inference resolves this insert as never
        .insert(partner)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async update(id: string, updates: PartnerUpdate) {
      const { data, error } = await supabase
        .from('partners')
        // @ts-ignore - Supabase table inference resolves this update as never
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async delete(id: string) {
      const { error } = await supabase.from('partners').delete().eq('id', id);
      if (error) throw error;
    },
  },

  // Team Members
  team: {
    async getAll(filters?: { is_active?: boolean }) {
      let query = supabase
        .from('team_members')
        .select('*')
        .order('sort_order', { ascending: true });

      if (filters?.is_active !== undefined) query = query.eq('is_active', filters.is_active);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    async create(member: TeamMemberInsert) {
      const { data, error } = await supabase
        .from('team_members')
        // @ts-ignore - Supabase table inference resolves this insert as never
        .insert(member)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async update(id: string, updates: TeamMemberUpdate) {
      const { data, error } = await supabase
        .from('team_members')
        // @ts-ignore - Supabase table inference resolves this update as never
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async delete(id: string) {
      const { error } = await supabase.from('team_members').delete().eq('id', id);
      if (error) throw error;
    },
  },

  // Inquiries
  inquiries: {
    async getAll(filters?: { status?: string; product_id?: string; assigned_to?: string; limit?: number; offset?: number }) {
      let query = supabase
        .from('inquiries')
        .select(`
          *,
          product:products(id, name, slug),
          assignee:users!inquiries_assigned_to_fkey(id, name, email)
        `)
        .order('created_at', { ascending: false });

      if (filters?.status) query = query.eq('status', filters.status);
      if (filters?.product_id) query = query.eq('product_id', filters.product_id);
      if (filters?.assigned_to) query = query.eq('assigned_to', filters.assigned_to);
      if (filters?.limit) query = query.limit(filters.limit);
      if (filters?.offset) query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    async getById(id: string) {
      const { data, error } = await supabase
        .from('inquiries')
        .select(`
          *,
          product:products(id, name, slug),
          assignee:users!inquiries_assigned_to_fkey(id, name, email)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },

    async create(inquiry: InquiryInsert) {
      const { data, error } = await supabase
        .from('inquiries')
        // @ts-ignore - Supabase table inference resolves this insert as never
        .insert(inquiry);

      if (error) throw error;
      return (data || inquiry) as Tables['inquiries']['Row'];
    },

    async update(id: string, updates: InquiryUpdate) {
      const { data, error } = await supabase
        .from('inquiries')
        // @ts-ignore - Supabase table inference resolves this update as never
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async updateStatus(id: string, status: InquiryStatus, notes?: string, assigned_to?: string) {
      const updates: InquiryUpdate & { contacted_at?: string } = { status };
      if (notes !== undefined) updates.notes = notes;
      if (assigned_to !== undefined) updates.assigned_to = assigned_to;
      if (status === 'contacted') updates.contacted_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('inquiries')
        // @ts-ignore - Supabase type inference issue
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async delete(id: string) {
      const { error } = await supabase.from('inquiries').delete().eq('id', id);
      if (error) throw error;
    },
  },

  // Contact Messages
  contactMessages: {
    async getAll(filters?: { status?: string; limit?: number; offset?: number }) {
      let query = supabase
        .from('contact_messages')
        .select(`
          *,
          replied_by_user:users!contact_messages_replied_by_fkey(id, name, email)
        `)
        .order('created_at', { ascending: false });

      if (filters?.status) query = query.eq('status', filters.status);
      if (filters?.limit) query = query.limit(filters.limit);
      if (filters?.offset) query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    async getById(id: string) {
      const { data, error } = await supabase
        .from('contact_messages')
        .select(`
          *,
          replied_by_user:users!contact_messages_replied_by_fkey(id, name, email)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },

    async create(message: ContactMessageInsert) {
      const { data, error } = await supabase
        .from('contact_messages')
        // @ts-ignore - Supabase table inference resolves this insert as never
        .insert(message);

      if (error) throw error;
      return (data || message) as Tables['contact_messages']['Row'];
    },

    async update(id: string, updates: Record<string, unknown>) {
      const { data, error } = await supabase
        .from('contact_messages')
        // @ts-ignore - Supabase table inference issue
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async delete(id: string) {
      const { error } = await supabase.from('contact_messages').delete().eq('id', id);
      if (error) throw error;
    },

    async reply(id: string, reply_message: string, replied_by: string) {
      const { data, error } = await supabase
        .from('contact_messages')
        // @ts-ignore - Supabase type inference issue
        .update({
          status: 'replied',
          reply_message,
          replied_by,
          replied_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
  },

  // Notifications
  notifications: {
    async getForUser(userId: string, limit = 50) {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .or(`user_id.eq.${userId},user_id.is.null`)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data;
    },

    async markAsRead(id: string) {
      const { data, error } = await supabase
        .from('notifications')
        // @ts-ignore - Supabase type inference issue
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async markAllAsRead(userId: string) {
      const { error } = await supabase
        .from('notifications')
        // @ts-ignore - Supabase type inference issue
        .update({ is_read: true, read_at: new Date().toISOString() })
        .or(`user_id.eq.${userId},user_id.is.null`)
        .eq('is_read', false);

      if (error) throw error;
    },

    async create(notification: NotificationInsert) {
      const { data, error } = await supabase
        .from('notifications')
        // @ts-ignore - Supabase table inference resolves this insert as never
        .insert(notification)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
  },

  // Users (admin only)
  users: {
    async getAll() {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },

    async create(user: Record<string, unknown>) {
      const { data, error } = await supabase
        .from('users')
        // @ts-ignore - Supabase table inference resolves this insert as never
        .insert(user)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async updateRole(id: string, role: UserRole) {
      const { data, error } = await supabase
        .from('users')
        // @ts-ignore - Supabase table inference resolves this update as never
        .update({ role })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },

    async delete(id: string) {
      const { error } = await supabase.from('users').delete().eq('id', id);
      if (error) throw error;
    },
  },
};

export default supabase;